// --- React & Redux
import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- Actions & Hooks
import { useProductGateActions } from 'datastage/store/productGate/useProductGate';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { usePermissions } from 'datastage/utils/permissions';
import { useProductGateData } from './hooks/useProductGateData';
import { useFeatureFlags } from 'datastage/context/FeatureFlagContext';
import { featureFlagService } from 'datastage/services/featureFlagService';
// --- External UI Libraries
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
import { Col, Row, Switch } from 'antd';
import ToggleSwitch from '@dtci/spellbook/ToggleSwitch';
// --- Components
import { PaginationBar } from 'datastage/components/PaginationBar';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
import { RightslineProductGateGrid } from './components/grid';
import { RightslineProductGateSearchForm } from './components/search-form';
import { JobStatusDrawer } from './components/status-drawer';
import { StaleTitlesDrawer } from './components/stale-titles-drawer';
import { TwoRowsLayout } from './layout/two-row';
// --- Utils
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';

/**
 * Rightsline Product Gate Page
 *
 * Features:
 * - Displays a paginated list of Rightsline product updates
 * - Provides filtering options with switches
 * - Allows exporting data to CSV
 * - Includes refresh functionality
 * - Implements refreshProductGate flag pattern for clean separation of concerns
 * - Persists page size preference to localStorage automatically
 * - Tracks page size and page number in URL
 *
 * @component
 * @returns {JSX.Element} The rendered Rightsline Product Gate page.
 */
export const RightslineProductGatePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showErrorToast } = useToasts();
    const { fetchProductGates, updateProductGate, productGateRange, itemsPerPage } =
        useProductGateData();
    const {
        totalPages,
        currentPage,
        filterForm,
        espnTitle,
        dataToggle,
        totalItems,
        refreshProductGate,
        pageSizeOptions,
    } = useSelector((state) => state.productGate);
    const { isRLProductGateAdmin, isRLProductGateEspnPlusUser, isRLProductGateEspnPlusReadOnlyUser } =
        usePermissions();
    const { isHyperlaneEnabled, refetch: refetchFeatureFlags } = useFeatureFlags();

    const showEspnToggle =
        isRLProductGateAdmin || isRLProductGateEspnPlusUser || isRLProductGateEspnPlusReadOnlyUser;
    const isFirstRender = useRef(true);
    const prevToggles = useRef({
        hideIgnored: !!filterForm.hideIgnored,
        hideCompleted: !!filterForm.hideCompleted,
        hideUpdates: !!filterForm.hideUpdates,
        hideNA: !!filterForm.hideNA,
    });

    /**
     * Refresh feature flags when page loads or on hard refresh.
     * Clears cache first to ensure fresh data is always fetched.
     * This ensures users see the latest Hyperlane settings without logging out.
     */
    useEffect(() => {
        featureFlagService.clearCache();
        refetchFeatureFlags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.productGate,
        itemsPerPage,
        rangeKey: 'productGateRange',
        onSync: updateProductGate,
    });


    /**
     * Listen for refreshProductGate flag and refresh the grid.
     * This pattern allows other components (like drawers) to trigger refreshes
     * without duplicating fetch logic.
     */
    useEffect(() => {
        if (refreshProductGate) {
            handleRefresh();
            updateProductGate({ refreshProductGate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshProductGate]);

    // Handle filter changes when any toggle switch is changed
    useEffect(() => {
        const toggles = ['hideIgnored', 'hideCompleted', 'hideUpdates', 'hideNA'];
        const changed = toggles.some((key) => prevToggles.current[key] !== filterForm[key]);
        if (isFirstRender.current) {
            isFirstRender.current = false;
        } else if (changed) {
            handleFilter();
        }
        toggles.forEach((key) => {
            prevToggles.current[key] = filterForm[key];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterForm]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchProductGates(nextPageRange);

        // Check for error (has code field) or null (network/auth error)
        if (!data || data.code) {
            // Error already shown by fetchProductGates
            return;
        }

        updateProductGate({
            productUpdates: data?.content ?? [],
            totalPages: data?.totalPages ?? 0,
            currentPage: data?.pageable?.pageNumber ?? 0,
            productGateRange: nextPageRange,
            totalItems: data?.totalElements ?? 0,
        });
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    /**
     * Apply filters and fetch product updates from API.
     * Resets to page 1 when filters change.
     */
    const handleFilter = async () => {
        const firstPageRange = getItemRange(0, itemsPerPage);
        const data = await fetchProductGates(firstPageRange);

        // Check for error (has code field) or null (network/auth error)
        if (!data || data.code) {
            // Error already shown by fetchProductGates
            return;
        }

        updateProductGate({
            productUpdates: data?.content ?? [],
            totalPages: data?.totalPages ?? 0,
            totalItems: data?.totalElements ?? 0,
            currentPage: data?.pageable?.pageNumber ?? 0,
            productGateRange: firstPageRange,
        });
    };

    /**
     * Export product updates to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        const exportRange = getAllItemsRange(totalItems);
        const data = await fetchProductGates(exportRange);

        // Check for error (has code field) or null (network/auth error)
        if (!data || data.code) {
            showErrorToast(data?.message || 'Failed to export product updates.');
            return;
        }

        // Check if we have data to export
        if (!data?.content || data.content.length === 0) {
            showErrorToast('No data available to export.');
            return;
        }

        exportToCsv(data.content, 'sourceReports_rightsLineProductUpdates');
    };

    /**
     * Refresh the product updates list from the API.
     */
    const handleRefresh = async () => {
        const data = await fetchProductGates(productGateRange);

        // Check for error (has code field) or null (network/auth error)
        if (!data || data.code) {
            return;
        }

        updateProductGate({
            productUpdates: data?.content ?? [],
            totalItems: data?.totalElements ?? 0,
            totalPages: data?.totalPages ?? 0,
            currentPage: data?.pageable?.pageNumber ?? 0,
        });
    };

    /**
     * Handle ESPN+ toggle switch change.
     */
    const handleESPNToggle = (value) => {
        const isEspnTitle = value === 'espnPlus';
        updateProductGate({
            dataToggle: value,
            espnTitle: isEspnTitle,
        });
    };

    /**
     * Handle individual filter switch changes.
     */
    const handleSwitchChange = (key, checked) => {
        updateProductGate({
            filterForm: {
                ...filterForm,
                [key]: !!checked,
            },
        });
    };

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <Typography variant="T50" style={{ marginLeft: 20 }}>
                            Rightsline Product Gate
                        </Typography>
                        {showEspnToggle ? (
                            <ToggleSwitch
                                className="sb-toggle-switch_custom"
                                handleSwitchChange={(value) => handleESPNToggle(value)}
                                initialValue={dataToggle}
                            >
                                <Button
                                    className="sb-toggle-switch_custom-option"
                                    sizeType="small"
                                    value="contentSales"
                                >
                                    Content Sales
                                </Button>
                                <Button
                                    className="sb-toggle-switch_custom-option"
                                    sizeType="small"
                                    value="espnPlus"
                                    style={{
                                        borderColor: dataToggle === 'espnPlus' ? 'red' : undefined,
                                    }}
                                >
                                    ESPN+
                                </Button>
                            </ToggleSwitch>
                        ) : (
                            <ToggleSwitch
                                className="sb-toggle-switch_custom"
                                initialValue={dataToggle}
                                handleSwitchChange={() => {}}
                            >
                                <Button
                                    style={{
                                        backgroundColor: '#fff',
                                        borderColor: '#fff',
                                        color: '#3611c8',
                                    }}
                                    className="sb-toggle-switch_custom-option"
                                    sizeType="small"
                                    value="contentSales"
                                    disabled
                                >
                                    Content Sales
                                </Button>
                            </ToggleSwitch>
                        )}
                    </div>
                </Col>
                <Col style={{ display: 'flex', gap: 8, marginRight: 0 }}>
                    <div style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                        {isHyperlaneEnabled && (
                            <Button
                                iconType="fa-solid fa-heart-pulse"
                                labelText="Stale Titles"
                                styleType="link"
                                onClick={() => updateProductGate({ isStaleTitlesOpen: true })}
                            >
                                Stale Titles
                            </Button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 20, marginRight: 20, marginTop: 10, marginLeft: isHyperlaneEnabled ? 10 : 0 }}>
                        {/* Switches */}
                        <Switch
                            checkedChildren="Hide Ignored"
                            unCheckedChildren="Hide Ignored"
                            checked={!!filterForm.hideIgnored}
                            onChange={(checked) => handleSwitchChange('hideIgnored', checked)}
                        />
                        <Switch
                            checkedChildren="Hide Completed"
                            unCheckedChildren="Hide Completed"
                            checked={!!filterForm.hideCompleted}
                            onChange={(checked) => handleSwitchChange('hideCompleted', checked)}
                        />
                        <Switch
                            checkedChildren="Hide Updates"
                            unCheckedChildren="Hide Updates"
                            checked={!!filterForm.hideUpdates}
                            onChange={(checked) => handleSwitchChange('hideUpdates', checked)}
                        />
                        <Switch
                            checkedChildren="Hide NA"
                            unCheckedChildren="Hide NA"
                            checked={!!filterForm.hideNA}
                            onChange={(checked) => handleSwitchChange('hideNA', checked)}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                        <Button
                            iconType="fa-solid fa-arrow-down-to-line"
                            labelText="Export"
                            styleType="link"
                            onClick={handleExport}
                            disabled={totalItems === 0}
                        >
                            Export
                        </Button>
                        <Button
                            iconType="fa-solid fa-rotate"
                            labelText="Refresh"
                            styleType="link"
                            onClick={handleRefresh}
                        >
                            Refresh
                        </Button>
                    </div>
                </Col>
            </Row>
            <TwoRowsLayout>
                <main data-position="top">
                    <RightslineProductGateSearchForm />
                </main>
                <main
                    data-position="bottom"
                    style={{
                        borderColor: dataToggle === 'espnPlus' ? 'red' : undefined,
                        borderWidth: dataToggle === 'espnPlus' ? '2px' : undefined,
                        borderStyle: dataToggle === 'espnPlus' ? 'solid' : undefined,
                    }}
                >
                    <RightslineProductGateGrid />
                </main>
            </TwoRowsLayout>
            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />

            {/* Job status drawer for viewing job details */}
            <JobStatusDrawer />

            {/* Stale Titles drawer for health monitoring */}
            <StaleTitlesDrawer />

            <ToastNotificationProvider />
        </>
    );
};
